import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import {translate} from '@jsverse/transloco';
import { RouterSegments } from 'src/app/models/router-segments';
import { Permission } from 'src/app/models/principal/permission';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import {ActivatedRoute, Router} from '@angular/router';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { ComponentType } from 'src/app/models/service/component-type';
import { SizingProgressDialogComponent } from 'src/app/components/sizing-progress-dialog/sizing-progress-dialog.component';
import { forkJoin, interval, startWith, switchMap, takeWhile} from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { SizingService } from 'src/app/services/api/sizing.service';
import { SizingConfirmDialogComponent } from 'src/app/components/sizing-confirm-dialog/sizing-confirm-dialog.component';
import { SizingDialogMode } from 'src/app/models/sizing';
import { JsonValidationService } from 'src/app/services/util/json-validation.service';
import { sizingTemplateSchema } from 'src/app/schemas/sizingTemplate-schema';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import {SizingErrorUtil} from 'src/app/services/util/sizing-error.util';
import { toTitleCase } from 'src/app/services/util/utils';
import { take } from 'rxjs';
import { SizingVideoTutorialDialogComponent } from 'src/app/main/environment-details/environment-sizing-documents/sizing-video-tutorial-dialog/sizing-video-tutorial-dialog.component';

const REQUIRED_PERMISSIONS = [
  Permission.CREATE_SANDBOX,
  Permission.CREATE_SERVICE_DEFINITION,
  Permission.VIEW_ENTERPRISE,
];

export enum DeployStep {
  INITIALIZED = 1,
  SIZING = 2,
  CONFIGURE = 3
}

export enum SizingStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

interface SizingMetric {
  key: string;
  label: string;
  value: any;
  type: string;
  overrideAllowed: boolean;
}

interface SizingSection {
  sectionTitle: string;
  metrics: SizingMetric[];
  isNested: boolean;
}

interface SizingCard {
  component: string;
  title: string;
  sections: SizingSection[];
}

enum SizingViewMode {
  FORM = 'FORM',
  JSON = 'JSON'
}


@Component({
  selector: 'app-deploy-sizing',
  templateUrl: './deploy-sizing.component.html',
  styleUrl: './deploy-sizing.component.scss',
  standalone: false
})

export class DeploySizingComponent implements OnInit {
  DeployStep = DeployStep;
  SizingStatus = SizingStatus;
  currentView: SizingViewMode = SizingViewMode.FORM;
  SizingViewMode = SizingViewMode;
  currentStep: DeployStep = DeployStep.INITIALIZED;
  sizingStatus: SizingStatus = SizingStatus.NOT_STARTED;
  sizingCreatedBy?: string;
  private destroyRef = inject(DestroyRef);
  breadcrumbItems: BreadcrumbItem[];
  RouterSegments = RouterSegments;
  tenantId: string;
  instanceId: string;
  tenant: string;
  instance: any;
  isLoading = true;
  blueprint: Blueprint;
  parameters: any;
  solution: SolutionVersion;
  ComponentType = ComponentType;
  isSizingCreated = false;
  sizingDocumentName?: string;
  sizingCreatedDate?: string;
  isSizingLoading = false;
  sizingTemplates: any;
  sizingCards: SizingCard[] = [];
  searchTerm: string = '';
  originalSizingCards: SizingCard[] = [];
  editorOptions = {
    theme: 'light',
    language: 'json',
    automaticLayout: true,
    readOnly: true,
  };

  jsonInput = {
    value: '',
    language: 'json'
  };
  isGlobalLoading = false;
  isJsonLoading = false;
  sizingDocumentId: any;
  allSizingRecords: any[] = [];
  isCompletedHandled = false;
  validSchema: boolean;
  templateResponse = true;
  errorsWithComponent:any;
  retrySizing: boolean;
  isSubmitClicked = false;
  private readonly VIDEO_DISMISSED_KEY = 'sizing-deploy-video-dismissed';
  showWatchTutorial = false;

  readonly INSTANCE_AVAILABLE_STATUS = [DeployStatus.DEPLOYED, DeployStatus.UPDATED,
      DeployStatus.UPDATE_FAILED, DeployStatus.TESTED, DeployStatus.TEST_FAILED,
      DeployStatus.CONFIGURED, DeployStatus.CONFIGURATION_FAILED,
      DeployStatus.FAILED, DeployStatus.FAILOVER_FAILED, DeployStatus.FAILBACK_FAILED, DeployStatus.PRE_FAILBACK_FAILED, DeployStatus.POST_FAILBACK_FAILED, DeployStatus.STOP_FAILED];

   constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private blueprintsService: BlueprintsService,
    private authService: AuthService,
    private instancesService: InstancesService,
    private dialog: MatDialog,
    private toastrService: CustomToastrService,
    private solutionsService: SolutionsService,
    private translocoService: TranslocoService,
    private sizingService: SizingService,
    private jsonSchemaValidator: JsonValidationService
  ) {

  }

   ngOnInit(): void {
    this.tenant = this.authService.principal.provider;
    this.instanceId = this.activatedRoute.snapshot.params.id;
    if (!localStorage.getItem(this.VIDEO_DISMISSED_KEY)) {
      this.openProvisionTutorial();
    } else {
      this.showWatchTutorial = true;
    }
    this.getParameters();
  }

  /* Transforms backend keys into user-friendly display labels */
  private formatLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  /* Normalizes a string for case-insensitive and format-insensitive comparison.*/
  private normalize(value: string): string {
  return value
    ?.toLowerCase()
    .replace(/[_:\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  }

  /* Returns true if the given deploy step is currently active */
  isActive(step: DeployStep): boolean {
    if (step === DeployStep.CONFIGURE) {
    return (
      this.currentStep === DeployStep.CONFIGURE &&
      this.sizingStatus === SizingStatus.COMPLETED
    );
  }
  return this.currentStep === step;
}

/* Returns true if the given deploy step is currently completed */
isCompleted(step: DeployStep): boolean {
   if (step === DeployStep.SIZING) {
    return this.sizingStatus === SizingStatus.COMPLETED;
  }

  if (step === DeployStep.CONFIGURE) {
    return this.currentStep > DeployStep.CONFIGURE;
  }

  return this.currentStep > step;
}

/* Returns true if the given deploy step is currently failed */
isFailed(step: DeployStep): boolean {
  if (step === DeployStep.SIZING) {
    return this.sizingStatus === SizingStatus.FAILED;
  }
  return this.currentStep === step;
}

/* Moves deploy workflow to next step */
next(): void {
  if (this.currentStep === DeployStep.INITIALIZED) {
    this.currentStep = DeployStep.SIZING;
    this.startSizingProcess();
    return;
  }

  if (this.currentStep === DeployStep.SIZING) {
    this.currentStep = DeployStep.CONFIGURE;
    return;
  }
}

/* Opens progress dialog and starts sizing template loading process */
private startSizingProcess(): void {
  this.isGlobalLoading = true;
  const dialogRef = this.dialog.open(SizingProgressDialogComponent, {
    width: '566px',
    height: '225px',
    disableClose: true,
    data: {
      title: translate('SIZING.VALIDATE'),
      progress: 0,
      showCount:false
    }
  });

  this.loadSizingdata(dialogRef);
}

  /* Fetch sizing template records from API and populate sizing cards */
  loadSizingdata(dialogRef?: any): void {
  this.isSizingLoading = true;
  this.isGlobalLoading = true;
  // display progress until getting api response & validated against schema
  let progress = 0;
  const progressInterval = setInterval(() => {
  if (dialogRef?.componentInstance && progress < 90) {
    progress += 1;
    dialogRef.componentInstance.progress = progress;
  }}, 300);
  this.sizingService
    .getSizingTemplateByInstanceId(this.instanceId,undefined, 'xxs-small')
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response: any) => {
        this.templateResponse = response.length > 0 ? true : false;
        if (!Array.isArray(response) || !this.templateResponse) {
          this.sizingStatus = SizingStatus.FAILED;
          this.isSizingLoading = false;
          this.isGlobalLoading = false;
          dialogRef?.close();
          return;
        }
        let records = Array.isArray(response) ? response : [];
        const templateSchema = this.jsonSchemaValidator.validate(records,sizingTemplateSchema);
        this.validSchema = templateSchema.valid;
        if (templateSchema.valid) {
          clearInterval(progressInterval);
          if (dialogRef?.componentInstance) { dialogRef.componentInstance.progress = 100;}
        this.allSizingRecords = records;

        const validRecords = records.filter(record =>
          record?.template &&
          Object.keys(record.template).length > 0 &&
          record.template?.properties &&
          Object.keys(record.template.properties).length > 0
        );

        const total = validRecords.length;

        if (dialogRef) {
            dialogRef.componentInstance.title = translate('SIZING.PREPARE');
            dialogRef.componentInstance.total = total;
            dialogRef.componentInstance.showCount = true;
          }
        let processed = 0;

        const processNext = () => {

          if (processed >= total) {

            dialogRef?.close();
            this.isSizingLoading = false;
            this.isGlobalLoading = false;

            this.sizingStatus = SizingStatus.NOT_STARTED;
            this.currentStep = DeployStep.SIZING;
            return;
          }

          const record = validRecords[processed];

          if (dialogRef?.componentInstance) {
            dialogRef.componentInstance.currentComponent =
              record.component;
            dialogRef.componentInstance.processed = processed + 1;
          }
          const card =this.buildSizingCards(record);
          this.originalSizingCards.push(card);
          this.sizingCards.push(card);
          processed++;

          setTimeout(processNext, 400);
        };

        processNext();
      } else {
        if (dialogRef) {
          dialogRef.close();
        }
        this.isSizingLoading = false;
        this.isGlobalLoading = false;
        this.sizingStatus = SizingStatus.FAILED;
        this.showErrorDialog(true,templateSchema,records);
      }
         
      },
      error: (error) => {
        clearInterval(progressInterval);
        this.retrySizing = true;
        this.sizingStatus = SizingStatus.FAILED;
        this.isSizingLoading = false;
        this.isGlobalLoading = false;
        dialogRef?.close();
        this.handleSubmitSizingError(error, translate('SIZING.VALIDATION_FAILED'));        
      }
    
    });
}

  openContactForm(): void {
    const url = `https://itsm-servicedesk.temenos.com`;
    window.open(url, '_blank');
  }

  openProvisionTutorial(): void {
    this.dialog.open(SizingVideoTutorialDialogComponent, {
      width: '720px',
      disableClose: false,
      panelClass: 'default-dialog-wrapper',
      data: {
        videoUrl: '/assets/media/Provision-Environment.mp4',
        title: translate('SIZING_VIDEO_TUTORIAL.TITLE'),
        description: translate('SIZING_VIDEO_TUTORIAL.DESCRIPTION')
      }
    }).afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        localStorage.setItem(this.VIDEO_DISMISSED_KEY, 'true');
        this.showWatchTutorial = true;
      });
  }

 /* Maps sizing template API response into structured sizing cards for UI rendering */
 private buildSizingCards(record: any): SizingCard  {
    const template = record?.template;
    if (!template?.properties) return null as any;

    const rootProperties = template.properties;
    const sections: SizingSection[] = [];

    const hasNestedSections = Object.values(rootProperties).some(
      (prop: any) => prop?.type === 'object' && prop?.properties
    );

    if (hasNestedSections) {

      Object.keys(rootProperties).forEach(sectionKey => {

        const sectionSchema = rootProperties[sectionKey];

        if (!sectionSchema?.properties) return;

        const metrics: SizingMetric[] = Object.keys(sectionSchema.properties).map(metricKey => {

          const prop = sectionSchema.properties[metricKey];

          return {
            key: metricKey,
            label: prop?.title || this.formatLabel(metricKey),
            value: prop?.default,
            type: prop?.type,
            overrideAllowed: prop?.['x-override-allowed'] ?? false
          };

        });

        sections.push({
          sectionTitle: sectionSchema.title || this.formatLabel(sectionKey),
          metrics,
          isNested: true
        });

      });

    }

    else {

      const metrics: SizingMetric[] = Object.keys(rootProperties).map(metricKey => {

        const prop = rootProperties[metricKey];

        return {
          key: metricKey,
          label: prop?.title || this.formatLabel(metricKey),
          value: prop?.default,
          type: prop?.type,
          overrideAllowed: prop?.['x-override-allowed'] ?? false
        };

      });

      sections.push({
        sectionTitle: template.title || record.component,
        metrics,
        isNested: false
      });

    }

    return {
      component: record.component,
      title: template.title,
      sections
    };
}



/* Handles sizing submission */
submitSizing(): void {
  if (this.isSubmitClicked) return;
  this.isSubmitClicked = true;
  this.sizingService.getSizingDocs(this.instanceId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(existingDocs => {
      const existingNames = (existingDocs || []).map((d: any) => d?.name?.toLowerCase().trim());
      const generatedName = this.generateNextSizingName(existingNames);

      const dialogRef = this.dialog.open(SizingConfirmDialogComponent, {
        width: '566px',
        height: '282px',
        disableClose: true,
        data: {
          documentName: generatedName,
          disableName: true,
          environmentSize: null,
          mode: SizingDialogMode.SUBMIT
        }
      });

      dialogRef.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result: any) => {
          if (!result?.documentName) {
            this.isSubmitClicked = false;
            return;
          }

          const progressDialogRef = this.dialog.open(SizingProgressDialogComponent, {
            width: '566px',
            height: '225px',
            disableClose: true,
            data: {
              title: translate('SIZING.SUBMIT'),
              progress: 0,
              showCount: false
            }
          });

          let progress = 0;
          const interval = setInterval(() => {
            if (progressDialogRef?.componentInstance && progress < 90) {
              progress += 5;
              progressDialogRef.componentInstance.progress = progress;
            }
          }, 200);

          this.sizingDocumentName = result?.documentName;
          const payload = this.buildSizingPayload();

          this.sizingStatus = SizingStatus.IN_PROGRESS;

          this.sizingService
            .postSizingDocs(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (response) => {
                clearInterval(interval);
                if (progressDialogRef?.componentInstance) {
                  progressDialogRef.componentInstance.progress = 100;
                }
                this.sizingDocumentId = response?.id;

                if (!this.sizingDocumentId) {
                  this.sizingStatus = SizingStatus.FAILED;
                  return;
                }

                setTimeout(() => {
                progressDialogRef.close();
                this.isSubmitClicked = false;
                this.startSizingPolling();
                this.sizingService.setSizingDocumentId(this.sizingDocumentId);
                }, 400);
              },
              error: (error) => {
                clearInterval(interval);
                progressDialogRef.close();
                this.isSubmitClicked = false;
                this.sizingStatus = SizingStatus.FAILED;
                this.handleSubmitSizingError(error);
              }
            });
        });
    });
}

private handleSubmitSizingError(err: any, title?: string): void {
  const apiError = err?.error?.error ?? err?.error ?? err;
  let errorArray = [];
  if (apiError?.details?.length) {
    errorArray = apiError.details.map((d: any) => {
      return {
        code: d.code ?? apiError.code,
        message: d.message,
        details: d.field
      };
    });
  } else if (apiError?.message) {
    errorArray = [{
      code: apiError?.code,
      message: apiError?.message,
      details: ''   
    }];
  }
  this.showErrorDialog(false, { title, descriptionError: errorArray})
}

/* Handles Polls sizing status */
private startSizingPolling(): void {

  let processed = 0;
  const total = this.sizingCards.length;
  let progress = 0;

  const progressDialogRef = this.dialog.open(SizingProgressDialogComponent, {
    width: '566px',
    height: '225px',
    disableClose: true,
    data: {
      title: translate('SIZING.GENERATE'),
      processed: 0,
      total: total,
      currentComponent: '',
      showCount: true
    }
  });

  let poll: ReturnType<typeof setInterval>;
  const pollFn = (): void => {
    this.sizingService
      .getSizingDocsById(this.instanceId, this.sizingDocumentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {

          if (!response) return;

          const completedCount = response.components?.length || 0;

          const animateProgress = () => {

            if (progress < completedCount) {

              progressDialogRef.componentInstance.processed = progress + 1;

              progressDialogRef.componentInstance.currentComponent =
                this.sizingCards[progress]?.component || '';

              progress++;

              setTimeout(animateProgress, 100);
            }
            else if (completedCount === total && !this.isCompletedHandled) {

              this.isCompletedHandled = true;
              clearInterval(poll);

              setTimeout(() => {
                progressDialogRef.close();
                this.sizingStatus = SizingStatus.COMPLETED;
                this.sizingCreatedDate = response?.createdAt;
                this.sizingCreatedBy = response?.createdBy;
                this.isSizingCreated = true;
                const title = translate('SIZING.SIZING_TITLE');
              const message = translate('SIZING.DOCUMENT_CREATED');
              this.toastrService.successMessage(message, title, this.instance?.name);
              }, 500);
            }
          };
          animateProgress();

          if (response.opstatus !== undefined && response.httpStatusCode !== undefined && (response.opstatus !== 0 || response.httpStatusCode !== 200)) {
            clearInterval(poll);
            progressDialogRef?.close();
            this.sizingStatus = SizingStatus.FAILED;
          }
        },
        error: () => {
          clearInterval(poll);
          progressDialogRef?.close();
          this.sizingStatus = SizingStatus.FAILED;
        }
      });
  };
  pollFn();
  poll = setInterval(pollFn, 3000);
}

/* Constructs POST payload from sizing cards */
private buildSizingPayload(): any {

  return {
    id: this.instanceId,
    name: this.sizingDocumentName,
    environmentSize: "xxs-small",
    components: this.originalSizingCards.map(card => ({
      component: card.component,
      document: this.buildComponentDocument(card)
    }))
  };
}

/* Maps a sizing card's sections and metrics into backend document format */
private buildComponentDocument(card: SizingCard): any {

  if (!card?.sections?.length) return {};

  const document: any = {};

  card.sections.forEach(section => {

    const sectionObject: any = {};

    section.metrics.forEach(metric => {

      if (metric.type === 'integer' || metric.type === 'number') {
        sectionObject[metric.key] = Number(metric.value);
      } else {
        sectionObject[metric.key] = metric.value;
      }

    });

    if (section.isNested) {
      document[section.sectionTitle] = sectionObject;
    } else {
      Object.assign(document, sectionObject);
    }

  });

  return document;
}


/* Generates the next Sizing_Default name by incrementing the max major version found in existing names */
private generateNextSizingName(existingNames: string[]): string {
  const majors = existingNames
    .filter(n => /^sizing_default_v\d+\.\d+$/i.test(n))
    .map(n => parseInt(n.replace(/^sizing_default_v(\d+)\.\d+$/i, '$1'), 10));
  const max = majors.length > 0 ? Math.max(...majors) : 0;
  return `Sizing_Default_v${max + 1}.0`;
}

/* Handles Sizing step button behavior based on current sizing status */
onSizingAction(): void {

    if (this.sizingStatus === SizingStatus.COMPLETED) {
    this.currentStep = DeployStep.CONFIGURE;
    return;
  }

  if (this.sizingStatus === SizingStatus.NOT_STARTED ||
    this.sizingStatus === SizingStatus.FAILED) {
    this.submitSizing();
  }
}

/* Applies search filter to sizing cards based on component, section, and metric values */
applySearch(): void {

    if (!this.searchTerm?.trim()) {
    this.sizingCards = [...this.originalSizingCards];
    return;
  }

  const term = this.normalize(this.searchTerm);

  this.sizingCards = this.originalSizingCards
    .map(card => {

      if (this.normalize(card.component).includes(term) ||
        this.normalize(card.title || '').includes(term)) {
        return card;
      }

      const filteredSections = card.sections
        .map(section => {

          const sectionMatches =
            this.normalize(section.sectionTitle).includes(term);

          const filteredMetrics = section.metrics
            .filter(metric => {
              const combined = this.normalize(
                `${metric.label} ${metric.value}`
              );
             return combined.includes(term);
          });

          if (sectionMatches) {
            return section;
          }

          if (filteredMetrics.length > 0) {
            return {
              ...section,
              metrics: filteredMetrics
            };
          }

          return null;
        })
        .filter(Boolean);

      if (filteredSections.length > 0) {
        return {
          ...card,
          sections: filteredSections
        };
      }

      return null;
    })
    .filter(Boolean) as SizingCard[];
}

/* Fetches instance parameters */
 getParameters(): any {
    this.instanceId = this.activatedRoute.snapshot.params.id;
    this.instancesService.getInstanceParameters(this.instanceId).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      this.parameters = data?.parameters;
      this.loadData();
    }, (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      this.loadData();
    });
  }


 /* Loads instance, blueprint, and solution data for the Deploy workflow */
   loadData(): void {
  const blueprintId = this.activatedRoute.snapshot.params.blueprintId;

  this.instancesService
    .getInstanceById(this.instanceId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(instance => {
      this.instance = instance;
      this.tenantId = instance.tenantId;

      if (
        this.instance.status !== DeployStatus.FAILED &&
        this.instance.status !== DeployStatus.INITIALIZED
      ) {
        this.router.navigate([
          '/',
          this.authService.principal.provider,
          RouterSegments.TENANT,
          this.instance.tenantId,
          RouterSegments.BLUEPRINTS,
          blueprintId
        ]);
        return;
      }

      forkJoin({
        blueprint: this.blueprintsService.getBlueprint(blueprintId),
        solution: this.solutionsService.getSolutionVersion(
          this.instance.solutionId,
          this.instance.solutionVersionId
        )
      })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({ blueprint, solution }) => {
            this.blueprint = blueprint;
            this.solution = solution;
            this.configureBreadcrumb();
          },
          complete: () => {
            this.isLoading = false;
          }
        });
    });
}

/* Safely formats display values for UI rendering */
  formatDisplay(value: any): string {
    if (!this.instance) return '';

    if (value === null || value === undefined || value === '' || value === 'undefined') {
      return '-';
    }

    return value;
  }

/* Formats a date into "MMM dd, yyyy" display format */
  private formatDate(date?: string | Date): string {
  if (!date) {
    return '';
  }

  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  }).replace(' ', ' ');
}

  /* Provides formatted contract end date for UI display */
  get formattedContractEndDate(): string {
    return this.formatDate(this.instance?.contractEndDate);
  }

  /* Switch between Form and Json tabs */
  switchView(mode: SizingViewMode): void {
    this.currentView = mode;
    if (mode === SizingViewMode.JSON && !this.jsonInput.value) {
      this.initializeJsonView();
    }
}

  /* Initializes the JSON view configuration */
  private initializeJsonView(): void {
  this.isJsonLoading = true;
  this.sizingService.getSizingTemplateByInstanceId(this.instanceId, undefined,'xxs-small').pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response) => {
        const jsonData = Array.isArray(response) ? response : [];
        this.jsonInput.value = JSON.stringify(jsonData, null, 2);
        this.isJsonLoading = false;
      },
      error: () => {
        this.jsonInput.value = '{}';
        this.isJsonLoading = false;
      }
    });
}

/* Triggers the download of the current JSON data */
  downloadJson(): void {
    if (!this.jsonInput?.value) return;
    const blob = new Blob([this.jsonInput.value],{ type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    const fileName = `${this.tenantId}-${this.blueprint?.name}-${this.instance?.name}-xxs-small`;
    downloadLink.download = `${fileName}.json`;
    downloadLink.click();
    window.URL.revokeObjectURL(url);
  }

  /* Prompts confirmation and navigates back to Blueprint details page */
  goBack(): void {
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('SIZING.CANCEL_TITLE'),
          message: translate('SIZING.CANCEL_MESSAGE'),
          confirmButtonText: translate('BUTTONS.ABORT')
        }
      })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirm => {
          if (confirm) {
            this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.blueprint?.id]);
          }
        });
    }

    /* Builds and sets breadcrumb navigation for the Deploy workflow */
  private configureBreadcrumb(): void {
      this.breadcrumbItems = [];
      this.breadcrumbItems.push({icon: 'assets/images/home_icon.svg', url:[RouterSegments.TENANT, this.tenantId] });
      this.breadcrumbItems.push({label: translate('TENANT_LAYOUT.MENU.SERVICE_DEFINITION'), url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION]},
      {label: this.blueprint?.name, url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION, this.blueprint?.id]});
      this.breadcrumbItems.push({label: translate('INSTANCE_SUMMARY.DEPLOY')});
    }
  /**
   * Opens error dialog and handles retry/report actions after dialog closes
   * @param response
   */
  showErrorDialog(initial:boolean,response?:any,records?:any): void {
    if (initial) {
    const templateerror = SizingErrorUtil.groupTemplateErrors(response?.errors ?? []);
    const groupedErrors = SizingErrorUtil.groupErrorsByParent(templateerror);
    this.errorsWithComponent = groupedErrors.map((group: any) => ({...group,component: records[group.recordIndex]?.componentDisplayName  || records[group.recordIndex]?.component || ''}));
    }
    const errorDialogRef = this.dialog.open(ErrorDialogComponent, {
      width: '566px',
      minHeight: '342px',
      panelClass: 'flex-dialog',
      data: {
        title: response?.title ? toTitleCase(response.title) : (initial ? translate('SIZING.VALIDATION_FAILED') : translate('SIZING.SUBMITTING_FAILED')),
        errorDetails : this.errorsWithComponent,
        descriptionError: response?.descriptionError
      }
    });
    errorDialogRef.afterClosed().subscribe(result => {
      if (result === 'retry') {
        this.sizingStatus = SizingStatus.NOT_STARTED;
        this.startSizingProcess();
      }
    });
  }
}
